import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge, Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const ROUTE = '/env/$environmentId/content/blocks';

/** Content → Content blocks (BB-57): reusable Liquid snippets with their key, current version and inclusion count. */
export function ContentBlocksPage() {
  const { environmentId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [query, setQuery] = useState('');
  const [archived, setArchived] = useState(false);
  const filters = { query, archived };
  const list = useQuery({ queryKey: ['content-blocks', environmentId, filters], queryFn: () => api.listContentBlocks(environmentId, filters) });

  return (
    <section>
      <h1>Content blocks</h1>
      <p className="hint">
        Reusable text that messages link with <code>{"{% content_block 'key' %}"}</code>. Running messages keep the version they were launched with until you
        update them from the block.
      </p>
      {has('content.edit') && (
        <Button variant="primary" onClick={() => void navigate({ to: '/env/$environmentId/content/blocks/new', params: { environmentId } })}>
          New content block
        </Button>
      )}
      <div className="filters">
        <label>
          Search <input aria-label="Search content blocks" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Archived
        </label>
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading content blocks…</p>
      ) : list.isError ? (
        <ErrorState title="Content blocks could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title={archived ? 'No archived content blocks' : 'No content blocks yet'} description={query ? 'Nothing matches the search.' : 'Create a block for text several messages share, such as a risk warning.'} />
      ) : (
        <table aria-label="Content blocks">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Key</th>
              <th scope="col">Version</th>
              <th scope="col">Used in</th>
              <th scope="col">Last edited</th>
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((b) => (
              <tr key={b.id}>
                <td>
                  <Link to="/env/$environmentId/content/blocks/$blockId" params={{ environmentId, blockId: b.id }}>
                    {b.name}
                  </Link>
                  {b.tags.map((x) => (
                    <Badge key={x}>{x}</Badge>
                  ))}
                  {b.description && <div className="hint">{b.description}</div>}
                </td>
                <td>
                  <code>{b.key}</code>
                </td>
                <td>v{b.currentVersion}</td>
                <td>{b.inclusionCount === 1 ? '1 message' : `${b.inclusionCount} messages`}</td>
                <td>
                  {b.updatedAt.slice(0, 10)}
                  {b.updatedBy?.email ? ` · ${b.updatedBy.email}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
